import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import fm from 'format-message';
import PropTypes from 'prop-types';
import textStyles from 'config/branding/textStyles';
import withHeightAuto from '@activebrands/core-web/components/withHeightAuto';
import useOnClickOutside from '@activebrands/core-web/hooks/useClickOutside';
import { styled, useStyletron } from '@activebrands/core-web/libs/styletron';
import { ADD_VOUCHER_COMBINATION_ERROR, addVoucher } from '@activebrands/core-web/state/basket/actions';
import ThemeButton from 'components/buttons/ThemeButton';
import StateIcon from 'components/icons/StateIcon';
import Input from 'components/inputs/Input';
import ActBonusVouchers from '../ActRewardsSection/ActBonusVouchers';

const ACT_REWARDS_ENABLED = process.env.REACT_APP_ENABLE_ACT_REWARDS === 'true';

const Wrapper = styled('div', {
    borderTop: '1px solid var(--color-border-checkout-subtle)',
    padding: '12px var(--margin-content)',
    ...textStyles['Misc/14_110_-0175'],
});

const ToggleButton = styled('button', {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
});

const Collapse = withHeightAuto(
    styled('div', {
        position: 'relative',
        height: '0', // Will work as default height
        overflow: 'hidden',
        transitionDuration: 'var(--duration-primary)',
        transitionProperty: 'height',
        transitionTimingFunction: 'var(--ease)',
    })
);

const Form = styled('form', {
    width: '100%',
    padding: '12px 0',
});

const Label = styled('label', {
    display: 'inline-block',
    color: 'var(--color-text-subtle)',
    marginBottom: '4px',
    ...textStyles['Misc/10_100_-00625'],
});

const Error = styled('p', {
    display: 'block',
    marginTop: '8px',
    color: 'var(--color-text-checkout-error)',
    pointerEvents: 'none',
});

const VouchersForm = ({ toggleLabel, placeholder, voucherLabel, isVouchersAvailable, ...rest }) => {
    const inputRef = useRef(null);
    const collapseRef = useRef(null);
    const dispatch = useDispatch();
    const [css] = useStyletron();
    const [state, setState] = useState({
        error: null,
        loading: false,
        open: false,
        success: false,
    });

    // Toggle voucher accordion
    const toggle = () => {
        setState(prevState => ({ ...prevState, open: !prevState.open }));
    };

    // reset Voucher input
    const resetInput = () => {
        inputRef.current.value = '';
    };

    // Reset error state
    const resetError = () => {
        setState(prevState => ({ ...prevState, error: null }));
    };

    // Close the accordion if clicked outside its ref
    useOnClickOutside(collapseRef, () => state.open && setState(prevState => ({ ...prevState, open: false })));

    useEffect(() => {
        // Clear error after 5sec
        if (state.error) {
            const timeOutId = setTimeout(() => resetError(), 5000);
            return () => clearTimeout(timeOutId);
        }
    }, [state.error]);

    useEffect(() => {
        // Clear success after 2sec
        if (state.success) {
            const timeOutId = setTimeout(() => {
                resetInput();
                setState(prevState => ({ ...prevState, success: false }));
            }, 2000);
            return () => clearTimeout(timeOutId);
        }
    }, [state.success]);

    // Handle voucher submit
    const handleSubmit = e => {
        e.preventDefault();

        setState(prevState => ({ ...prevState, loading: true, error: null }));

        // dispatch the voucher code to addVoucher. Only interested in the status codes.
        dispatch(addVoucher(inputRef.current?.value))
            .then(response => {
                if (response.status === 200) {
                    setState(prevState => ({ ...prevState, loading: false, success: true }));
                } else {
                    throw response;
                }
            })
            .catch(e => {
                if (e === ADD_VOUCHER_COMBINATION_ERROR) {
                    setState(prevState => ({
                        ...prevState,
                        loading: false,
                        error: fm('We were not able to combine these discount codes.'),
                    }));
                } else {
                    setState(prevState => ({
                        ...prevState,
                        loading: false,
                        error: fm('Invalid discount code. Please try again.'),
                    }));
                }
            })
            .finally(() => {
                resetInput();
            });
    };

    return (
        <Wrapper {...rest}>
            <ToggleButton typ="button" onClick={toggle}>
                <span>{toggleLabel}</span>
                <StateIcon size="12px" state={!state.open ? 'open' : 'close'} />
            </ToggleButton>

            <Collapse height={state.open ? 'auto' : 0}>
                <Form onSubmit={handleSubmit}>
                    <div className={css({ display: 'flex', alignItems: 'flex-end' })}>
                        <div className={css({ flexGrow: 1 })}>
                            <Label htmlFor="voucher_input_checkout">{voucherLabel}</Label>
                            <Input
                                required
                                $style={{ marginTop: 0 }}
                                id="voucher_input_checkout"
                                placeholder={!state.error ? placeholder : ''}
                                inputRef={inputRef}
                                type="text"
                                onFocus={() => resetError()}
                            />
                        </div>
                        <ThemeButton
                            $style={{ margin: 0, padding: '7px 12px' }}
                            disabled={state.loading || state.error || state.success}
                            fontKeys="Misc/16_100_-02"
                            theme="defaultsecondary"
                            type="submit"
                            onClick={e => e.currentTarget.blur()}
                        >
                            {fm('Submit')}
                            <StateIcon state={state.loading ? 'loading' : state.error ? 'error' : 'arrow-right'} />
                        </ThemeButton>
                    </div>
                    {state.error && <Error>{state.error}</Error>}
                    {ACT_REWARDS_ENABLED && (
                        <ActBonusVouchers isVouchersAvailable={isVouchersAvailable} $style={{ marginTop: '12px' }} />
                    )}
                </Form>
            </Collapse>
        </Wrapper>
    );
};

VouchersForm.propTypes = {
    isVouchersAvailable: PropTypes.bool,
    placeholder: PropTypes.string,
    toggleLabel: PropTypes.string,
    voucherLabel: PropTypes.string,
};

export default VouchersForm;
